import type { DraftFeedbackTarget } from "./Feedback";

/**
 * Discriminated union describing the content shown in the MyOutlook detail pane.
 * The detail pane replaces the briefing/schedule area when a user drills into
 * a specific item (e.g. clicks an actionable email pill).
 *
 * Add new variants here as more content types are supported.
 */
export type DetailPaneContent = MailReadingPaneContent | TeamsReplyPaneContent;

export interface DetailPaneMailLoadErrorDetails {
    errorName?: string;
    errorMessage?: string;
    messageText?: string;
    responseCode?: string;
}

export interface DetailPaneMailLoadDiagnostics extends DetailPaneMailLoadErrorDetails {
    contentType: "mailReadingPane";
    viewMode: "conversation" | "item";
    initialMailStoreLookup: "hit" | "miss";
    loaderResult: "success" | "failure";
    failureReason?: "mailServiceError" | "contentMissingAfterLoad";
}

export type DetailPaneLoadState = {
    state: "NotLoaded";
} | {
    state: "Loading";
    contentId: string;
} | {
    state: "Loaded";
    contentId: string;
    diagnostics?: DetailPaneMailLoadDiagnostics;
} | {
    state: "Error";
    contentId: string;
    diagnostics: DetailPaneMailLoadDiagnostics;
};

/** Show the mail reading pane for a specific message. */
export interface MailReadingPaneContent {
    type: "mailReadingPane";
    /** EWS item ID of the message to display. */
    itemId: string;
    /** Optional conversation ID for threading context. */
    conversationId?: string;
    /** Subject line shown in the detail pane header. */
    subject?: string;
}

export type TeamsReplyCopyState = "idle" | "copying" | "copied" | "failed";

/** Show a generated Teams reply before handing the user off to Teams. */
export interface TeamsReplyPaneContent {
    type: "teamsReplyPane";
    /** Unique, non-content identity for this pane instance and its async copy completion. */
    paneId: string;
    /** OPX copy-and-open destination, validated without reconstructing channel routing. */
    opxDestinationUrl?: string;
    /** Opaque identity for the exact generated reply. */
    generatedOutputId?: string;
    /** Teams chat/thread id used as the deep-link conversation target. */
    chatOrThreadId: string;
    /** Exact message id when the service provides both chat and thread ids. */
    messageId?: string;
    /** Exact plain text returned by the service. */
    replyText: string;
    /** Whether the user has copied the draft, and the result of that clipboard write. */
    copyState: TeamsReplyCopyState;
    /** Non-content diagnostic correlation id for OCV. */
    requestId?: string;
    /** Exact generated-output target for the POS feedback dual-write. */
    feedbackTarget?: DraftFeedbackTarget;
}

/**
 * Stable identity for a detail-pane content, used for scroll / session tracking.
 * `itemId` for the mail pane and the per-open `paneId` for generated Teams replies.
 */
export function getDetailPaneContentId(content: DetailPaneContent): string {
    switch (content.type) {
        case "teamsReplyPane":
            return content.paneId;
        default:
            return content.itemId;
    }
}
